// S9 — Health Dashboard.
//
// Four summary cards at the top, four tabs below: Camera Health, NVR
// Health, Active Alerts, Exception Report. The dashboard polls every
// 30s (cameras + alerts) / 60s (summary + NVR) via the react-query
// hooks in api/health.ts.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangleIcon,
  BellIcon,
  CameraIcon,
  CheckCircle2Icon,
  CircleDotIcon,
  DownloadIcon,
  ServerIcon,
  ShieldAlertIcon,
} from "lucide-react";

import { useCameras } from "@/api/cameras";
import {
  type HealthAlertItem,
  type NvrHealthItem,
  useExceptionReport,
  useHealthAlerts,
  useHealthCameras,
  useHealthSummary,
  useNvrHealthList,
  usePollCamera,
  useResolveAlert,
} from "@/api/health";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { DeviceContextLabel } from "@/components/DeviceContextLabel";
import { nvrKey, nvrFilterOptions } from "@/lib/nvrKey";
import { useFormatTimestamp } from "@/utils/datetime";
import { cn } from "@/lib/utils";


type Tab = "cameras" | "nvr" | "alerts" | "exception";


function pctTone(pct: number, hi = 90, mid = 70): "ok" | "warn" | "error" {
  if (pct >= hi) return "ok";
  if (pct >= mid) return "warn";
  return "error";
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = "default",
}: {
  icon: typeof CameraIcon;
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "ok" | "warn" | "error";
}) {
  const toneClass = {
    default: "text-foreground",
    ok: "text-emerald-600",
    warn: "text-yellow-600",
    error: "text-red-600",
  }[tone];
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3 p-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className={cn("mt-1 text-2xl font-semibold", toneClass)}>{value}</p>
          {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className="rounded-md bg-primary/5 p-2 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}


function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-3 py-2 text-sm transition",
        active
          ? "border-[#ff6d00] font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}


// ---------------------------------------------------------------------------
// Tab 1 — Camera Health table
// ---------------------------------------------------------------------------
function CamerasTab() {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [nvrFilter, setNvrFilter] = useState<string>("");
  const cameras = useHealthCameras(
    statusFilter ? { status: statusFilter } : undefined,
  );
  const poll = usePollCamera();
  const formatTs = useFormatTimestamp();

  const allRows = cameras.data?.items ?? [];
  const nvrOptions = useMemo(() => nvrFilterOptions(allRows), [allRows]);
  const rows = useMemo(
    () => allRows.filter((c) => !nvrFilter || nvrKey(c) === nvrFilter),
    [allRows, nvrFilter],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-xs"
        >
          <option value="">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="unknown">Unknown</option>
        </select>
        <select
          value={nvrFilter}
          onChange={(e) => setNvrFilter(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-xs"
        >
          <option value="">All NVRs</option>
          {nvrOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">
          {rows.length} camera{rows.length === 1 ? "" : "s"}
        </span>
      </div>
      <Card>
        <CardContent className="p-0">
          {cameras.isLoading ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              No cameras match the filter.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Camera</th>
                  <th className="px-3 py-2 text-left">Location (Site › NVR › Ch)</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Recording</th>
                  <th className="px-3 py-2 text-left">Last Check</th>
                  <th className="px-3 py-2 text-right">Alerts</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.camera_id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <Link
                        to={`/health/cameras/${c.camera_id}`}
                        className="text-[#ff6d00] hover:underline"
                      >
                        {c.name}
                      </Link>
                      {c.entity_name && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          · {c.entity_name}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <DeviceContextLabel
                        camera={{
                          id: c.camera_id,
                          name: c.name,
                          site_name: c.site_name,
                          nvr_make: c.nvr_make,
                          nvr_model: c.nvr_model,
                          nvr_serial: c.nvr_serial,
                          channel_no: c.channel_no,
                        }}
                        className="max-w-[20rem]"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={c.status} dot />
                    </td>
                    <td className="px-3 py-2">
                      {c.is_recording ? (
                        <Badge variant="online">REC</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {c.last_health_check_at
                        ? formatTs(c.last_health_check_at, "relative")
                        : "never"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c.active_alert_count > 0 ? (
                        <Badge variant="error">{c.active_alert_count}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => poll.mutate(c.camera_id)}
                      >
                        Poll Now
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Tab 2 — NVR Health
// ---------------------------------------------------------------------------
function NvrTab() {
  const nvrs = useNvrHealthList();
  const formatTs = useFormatTimestamp();
  return (
    <div className="space-y-3">
      {nvrs.isLoading ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Loading…
          </CardContent>
        </Card>
      ) : (nvrs.data?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            No NVR snapshots yet. The agent-level poll runs whenever a
            slink agent reports in.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {(nvrs.data ?? []).map((n) => (
            <NvrCard key={n.agent_id ?? Math.random()} nvr={n} formatTs={formatTs} />
          ))}
        </div>
      )}
    </div>
  );
}

function NvrCard({
  nvr,
  formatTs,
}: {
  nvr: NvrHealthItem;
  formatTs: ReturnType<typeof useFormatTimestamp>;
}) {
  const hddPct = nvr.hdd_used_pct ?? 0;
  const hddTone = hddPct >= 90 ? "error" : hddPct >= 80 ? "warn" : "ok";
  const toneColor = {
    ok: "bg-emerald-500",
    warn: "bg-yellow-500",
    error: "bg-red-500",
  }[hddTone];
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{nvr.device_name ?? "Unknown NVR"}</p>
            <p className="text-[11px] text-muted-foreground">
              {nvr.ip_address ?? "—"}
            </p>
          </div>
          <Badge variant={nvr.is_online ? "online" : "error"}>
            {nvr.is_online ? "ONLINE" : "OFFLINE"}
          </Badge>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span>HDD usage</span>
            <span>{nvr.hdd_used_pct?.toFixed(0) ?? "—"}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded bg-muted">
            <div
              className={cn("h-full", toneColor)}
              style={{ width: `${Math.min(100, hddPct)}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            HDD status: <Badge variant="outline">{nvr.hdd_status}</Badge>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-muted-foreground">Channels</p>
            <p>
              {nvr.recording_channel_count}/{nvr.channel_count}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Last poll</p>
            <p>
              {nvr.checked_at
                ? formatTs(nvr.checked_at, "relative")
                : "—"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Tab 3 — Active Alerts
// ---------------------------------------------------------------------------
function severityBadge(s: HealthAlertItem["severity"]) {
  const tone = s === "critical" ? "error" : s === "warning" ? "warning" : "outline";
  return <Badge variant={tone}>{s.toUpperCase()}</Badge>;
}

function AlertsTab() {
  const [severityFilter, setSeverityFilter] = useState<string>("");
  const alerts = useHealthAlerts(
    severityFilter ? { severity: severityFilter } : undefined,
  );
  const resolve = useResolveAlert();
  const formatTs = useFormatTimestamp();
  // U2 (client-side) — resolve the alert's camera_id to its name via the cameras list
  // (the alert payload carries only the id), same join AlertsPage uses.
  const cams = useCameras({ limit: 500 });
  const cameraNameById = useMemo(
    () => new Map((cams.data?.items ?? []).map((c) => [c.id, c.name])),
    [cams.data],
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-xs"
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
        <span className="text-xs text-muted-foreground">
          Auto-refreshes every 60s
        </span>
      </div>
      <Card>
        <CardContent className="p-0">
          {(alerts.data?.items?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              No active alerts.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Severity</th>
                  <th className="px-3 py-2 text-left">Camera</th>
                  <th className="px-3 py-2 text-left">Message</th>
                  <th className="px-3 py-2 text-left">First Seen</th>
                  <th className="px-3 py-2 text-right">Occurrences</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {(alerts.data?.items ?? []).map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs">{a.alert_type}</td>
                    <td className="px-3 py-2">{severityBadge(a.severity)}</td>
                    <td className="px-3 py-2 text-xs">
                      {a.camera_id ? (
                        <Link
                          to={`/health/cameras/${a.camera_id}`}
                          className="text-[#ff6d00] hover:underline"
                        >
                          {cameraNameById.get(a.camera_id) ?? a.camera_id.slice(0, 8)}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{a.message}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {a.first_seen_at
                        ? formatTs(a.first_seen_at, "relative")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {a.occurrence_count}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resolve.mutate(a.id)}
                      >
                        Resolve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Tab 4 — Exception Report
// ---------------------------------------------------------------------------
function ExceptionTab() {
  const [uptimeT, setUptimeT] = useState(80);
  const [recT, setRecT] = useState(80);
  const report = useExceptionReport({
    uptime_threshold: uptimeT,
    recording_threshold: recT,
  });

  function exportCsv() {
    const rows = report.data?.items ?? [];
    const headers = [
      "Camera", "Entity", "Uptime %", "Recording %", "Issues",
    ];
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push([
        JSON.stringify(r.name),
        JSON.stringify(r.entity_name ?? ""),
        r.uptime_pct.toFixed(1),
        r.recording_pct.toFixed(1),
        JSON.stringify(r.issues.join("; ")),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `health-exception-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs">
          Uptime threshold:
          <input
            type="number"
            min={0}
            max={100}
            value={uptimeT}
            onChange={(e) => setUptimeT(Number(e.target.value))}
            className="ml-1 w-16 rounded border border-border bg-background px-1 py-0.5 text-xs"
          />
          %
        </label>
        <label className="text-xs">
          Recording threshold:
          <input
            type="number"
            min={0}
            max={100}
            value={recT}
            onChange={(e) => setRecT(Number(e.target.value))}
            className="ml-1 w-16 rounded border border-border bg-background px-1 py-0.5 text-xs"
          />
          %
        </label>
        <Button
          variant="outline"
          size="sm"
          onClick={exportCsv}
          disabled={(report.data?.items?.length ?? 0) === 0}
        >
          <DownloadIcon className="mr-1 h-3.5 w-3.5" /> Export CSV
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          {(report.data?.items?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-sm text-emerald-700">
              All cameras meet the thresholds.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Camera</th>
                  <th className="px-3 py-2 text-right">Uptime %</th>
                  <th className="px-3 py-2 text-right">Recording %</th>
                  <th className="px-3 py-2 text-left">Issues</th>
                </tr>
              </thead>
              <tbody>
                {(report.data?.items ?? []).map((r) => {
                  const severity =
                    r.uptime_pct < 60 || r.recording_pct < 60
                      ? "bg-red-50"
                      : "bg-yellow-50";
                  return (
                    <tr
                      key={r.camera_id}
                      className={cn("border-b last:border-0", severity)}
                    >
                      <td className="px-3 py-2 text-xs">
                        <Link
                          to={`/health/cameras/${r.camera_id}`}
                          className="text-[#ff6d00] hover:underline"
                        >
                          {r.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {r.uptime_pct.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {r.recording_pct.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-xs">{r.issues.join("; ")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function HealthDashboard() {
  const summary = useHealthSummary();
  const [tab, setTab] = useState<Tab>("cameras");
  const s = summary.data;
  const onlinePct = useMemo(() => {
    if (!s || s.total_cameras === 0) return 0;
    return Math.round((s.online_count / s.total_cameras) * 100);
  }, [s]);
  const recordingPct = useMemo(() => {
    if (!s || s.total_cameras === 0) return 0;
    return Math.round((s.recording_count / s.total_cameras) * 100);
  }, [s]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Health</h1>
        <p className="text-sm text-muted-foreground">
          Camera + NVR health, active alerts, and the compliance
          exception report. Auto-refreshes every minute.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={CameraIcon}
          label="Cameras Online"
          value={s ? `${s.online_count}/${s.total_cameras}` : "—"}
          sub={`${onlinePct}%`}
          tone={pctTone(onlinePct)}
        />
        <StatCard
          icon={CircleDotIcon}
          label="Recording Active"
          value={s ? `${s.recording_count}/${s.total_cameras}` : "—"}
          sub={`${recordingPct}%`}
          tone={pctTone(recordingPct)}
        />
        {/* T3a — LABELLED, NOT RECONCILED. This counts `health_alerts`, from
            the health monitor. The Dashboard's alert tile counts
            `alert_events`, from the user-configurable rules engine. They
            measured 44 active / 40 critical here against 1 open / 0 critical
            there, on the same fleet at the same moment, and both were right.
            The two are not comparable and the labels now say so rather than
            inviting the subtraction. See docs/audit/UI-AUDIT-TRANCHES.md T3a. */}
        <StatCard
          icon={BellIcon}
          label="Health Alerts (active)"
          value={s ? String(s.active_alert_count) : "—"}
          sub={s
            ? `from health monitoring · ${s.active_alerts_by_severity.critical} crit · ${s.active_alerts_by_severity.warning} warn`
            : undefined}
          tone={
            s && s.active_alerts_by_severity.critical > 0
              ? "error"
              : s && s.active_alerts_by_severity.warning > 0
                ? "warn"
                : "ok"
          }
        />
        {/* T3b — this tile was labelled "Overall Compliance" while the number
            it renders is `overall_uptime_pct`: the mean of
            CameraUptimeSummary.uptime_pct. That is UPTIME, not compliance.

            The Dashboard's "Recording Compliance" tile is the mean of
            RecordingComplianceSnapshot.compliance_pct — a different table, a
            different question. Two tiles both saying "compliance" and reading
            56.3% and 0% invited the conclusion that one of them was broken.
            Neither was. Naming the measurement removes the false comparison
            without collapsing either number. */}
        <StatCard
          icon={CheckCircle2Icon}
          label="Camera Uptime (today)"
          value={s ? `${s.overall_uptime_pct.toFixed(1)}%` : "—"}
          sub="Mean uptime across all cameras — not recording compliance"
          tone={s ? pctTone(s.overall_uptime_pct) : "default"}
        />
      </div>

      <div className="flex flex-wrap gap-2 border-b">
        <TabButton active={tab === "cameras"} onClick={() => setTab("cameras")}>
          <CameraIcon className="mr-1 inline h-3.5 w-3.5" /> Camera Health
        </TabButton>
        <TabButton active={tab === "nvr"} onClick={() => setTab("nvr")}>
          <ServerIcon className="mr-1 inline h-3.5 w-3.5" /> NVR Health
        </TabButton>
        <TabButton active={tab === "alerts"} onClick={() => setTab("alerts")}>
          <AlertTriangleIcon className="mr-1 inline h-3.5 w-3.5" /> Active Alerts
        </TabButton>
        <TabButton active={tab === "exception"} onClick={() => setTab("exception")}>
          <ShieldAlertIcon className="mr-1 inline h-3.5 w-3.5" /> Exception Report
        </TabButton>
      </div>

      {tab === "cameras" && <CamerasTab />}
      {tab === "nvr" && <NvrTab />}
      {tab === "alerts" && <AlertsTab />}
      {tab === "exception" && <ExceptionTab />}
    </div>
  );
}
